import { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { useSliderDragging } from '../Slider/useSliderDragging';
import { isTouchDevice } from '../../utils/device';

import { Option } from './Option';
import type { OptionProps } from './Option';

import * as styles from './SeasonPicker.module.scss';

interface SelectProps<T> {
  value: T;
  options: {
    label: ReactNode;
    value: T;
    title?: string;
  }[];
  onChange: (value: T) => void;
  size?: 'large' | 'small';
}

export const SeasonPicker = <T extends string | number>({
  options,
  value,
  onChange,
  size = 'large',
}: SelectProps<T>): ReactElement | null => {
  const { t } = useTranslation();
  const startSentinelRef = useRef<HTMLDivElement>(null!);
  const endSentinelRef = useRef<HTMLDivElement>(null!);
  const sliderRef = useRef<HTMLUListElement>(null!);
  const [atStart, setAtStart] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === startSentinelRef.current) {
            setAtStart(entry.isIntersecting);
          } else if (entry.target === endSentinelRef.current) {
            setAtEnd(entry.isIntersecting);
          }
        });
      },
      { root: sliderRef.current, rootMargin: '0px 1px 0px 1px' }
    );

    observer.observe(startSentinelRef.current);
    observer.observe(endSentinelRef.current);

    return () => observer.disconnect();
  }, []);

  const { handleMouseDown, handleClickCapture, handleDragStartCapture } =
    useSliderDragging({ sliderRef });

  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [value, options]);

  const handleOptionClick: OptionProps<T>['onOptionClick'] = useCallback(
    ({ value: newValue, node }) => {
      onChange(newValue);

      const { offsetLeft, clientWidth } = node.current;

      const scrollDiff =
        sliderRef.current.scrollLeft - offsetLeft + clientWidth;

      sliderRef.current.scrollBy({
        left: -1 * scrollDiff,
        behavior: 'smooth',
      });
    },
    [onChange]
  );

  const displayLabel = size === 'large';

  return (
    <div className={styles.container} data-nosnippet>
      {displayLabel && <span className={styles.label}>{t('Season')}</span>}
      <div
        className={styles.sliderWrapper}
        at-start={String(atStart)}
        at-end={String(atEnd)}
      >
        <ul
          className={styles.slider}
          ref={sliderRef}
          onMouseDown={!isTouchDevice ? handleMouseDown : undefined}
          onClickCapture={!isTouchDevice ? handleClickCapture : undefined}
          onDragStartCapture={
            !isTouchDevice ? handleDragStartCapture : undefined
          }
        >
          <div ref={startSentinelRef} />
          {options.map(({ value, label, title }) => (
            <Option
              value={value}
              isSelected={value === selectedOption?.value}
              onOptionClick={handleOptionClick}
              title={title}
              key={value}
            >
              {label}
            </Option>
          ))}
          <div ref={endSentinelRef} />
        </ul>
      </div>
    </div>
  );
};
